"use client";

import ArrowRight from "~/assets/icons/arrow-right.svg";
import React, { type Dispatch, type SetStateAction } from "react";
import { FullscreenModalContentWrapper } from "~/components/ui/FullscreenModal";
import SuccessFormCard from "~/components/ui/SuccessFormCard";
import ErrorFormCard from "~/components/ui/ErrorFormCard";
import { useFormContext } from "react-hook-form";
import { AiFillExclamationCircle } from "react-icons/ai";
import { IoIosCheckmarkCircle } from "react-icons/io";

export type ContactFormInput = {
  name?: string;
  email: string;
  message: string;
};

export default function ContactForm({
  isSubmitted,
  hasError,
  setErrorAction,
  closeModalAction,
  success,
}: {
  isSubmitted: boolean;
  hasError?: boolean;
  setErrorAction: Dispatch<SetStateAction<boolean | null>>;
  closeModalAction: () => void;
  success: boolean;
}) {
  const {
    register,
    getFieldState,
    watch,
    formState,
    formState: { errors },
  } = useFormContext<ContactFormInput>();

  if (success) {
    return (
      <>
        <FullscreenModalContentWrapper shouldShift={true}>
          <SuccessFormCard
            closeModalAction={closeModalAction}
            message={"Zpráva byla úspěšně odeslána."}
            buttonMessage={"Zpět na Kontakt"}
          />
        </FullscreenModalContentWrapper>
      </>
    );
  }

  return (
    <>
      <FullscreenModalContentWrapper shouldShift={true}>
        {!success && hasError && (
          <ErrorFormCard setErrorAction={setErrorAction} />
        )}
        <div className="mb-6 flex flex-col items-stretch">
          <label
            className={
              "mb-2 inline-flex items-center gap-1 align-middle font-bold" +
              (errors.name
                ? " text-warning-primary"
                : getFieldState("name").isDirty && (watch("name")?.length?? 0) > 0
                  ? " text-success-primary"
                  : " text-primary")
            }
            htmlFor="name"
          >
            Jméno (volitelné){" "}
            {errors.name ? (
              <AiFillExclamationCircle />
            ) : (
              getFieldState("name", formState).isDirty && (watch("name")?.length?? 0) > 0 && (
                <IoIosCheckmarkCircle />
              )
            )}
          </label>
          <input
            {...register("name")}
            className={
              "focus:shadow-outline rounded border-2 border-gray-100 px-3 py-4 " +
              "leading-tight text-black focus:outline-none" +
              (errors.name
                ? " error-input-state"
                : !getFieldState("name", formState).isDirty || (watch("name")?.length?? 0) <= 0
                  ? " border-gray-100 bg-gray-100 hover:bg-gray-200 focus:border-[#007BB3]"
                  : " success-input-state")
            }
            id="name"
            type="text"
            placeholder="Jméno"
          />
        </div>

        <div className="mb-6 flex flex-col items-stretch">
          <label
            className={
              "mb-2 inline-flex items-center gap-1 align-middle font-bold" +
              (errors.email ||
              (getFieldState("email").isDirty && watch("email").length <= 0)
                ? " text-warning-primary"
                : getFieldState("email").isDirty
                  ? " text-success-primary"
                  : " text-primary")
            }
            htmlFor="email"
          >
            E-mail{" "}
            {errors.email ||
            (getFieldState("email", formState).isDirty &&
              watch("email").length <= 0) ? (
              <AiFillExclamationCircle />
            ) : (
              getFieldState("email", formState).isDirty && (
                <IoIosCheckmarkCircle />
              )
            )}
          </label>
          <input
            {...register("email", {
              required: "Toto pole je povinné!",
              pattern: {
                value: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                message: 'Zadejte platný e-mail!',
              },
            })}
            className={
              "focus:shadow-outline rounded border-2 border-gray-100 px-3 py-4 " +
              "leading-tight text-black focus:outline-none" +
              (errors.email ||
              (getFieldState("email", formState).isDirty &&
                watch("email").length <= 0)
                ? " error-input-state"
                : !getFieldState("email", formState).isDirty
                  ? " input"
                  : " success-input-state")
            }
            type="text"
            placeholder="E-mail"
          />
          {(errors.email ??
            (getFieldState("email", formState).isDirty &&
              watch("email").length <= 0)) && (
            <span className="mt-2 text-warning-primary">
              {errors.email?.message ?? "Toto pole je povinné!"}
            </span>
          )}
        </div>

        <div className="mb-4 flex flex-col items-stretch">
          <label
            className={
              "mb-2 inline-flex items-center gap-1 align-middle font-bold" +
              (errors.message ||
              (getFieldState("message").isDirty && watch("message").length <= 0)
                ? " text-warning-primary"
                : getFieldState("message").isDirty
                  ? " text-success-primary"
                  : " text-primary")
            }
            htmlFor="message"
          >
            Zpráva{" "}
            {errors.message ||
            (getFieldState("message", formState).isDirty &&
              watch("message").length <= 0) ? (
              <AiFillExclamationCircle />
            ) : (
              getFieldState("message", formState).isDirty && (
                <IoIosCheckmarkCircle />
              )
            )}
          </label>
          <textarea
            {...register("message", { required: true })}
            className={
              "focus:shadow-outline rounded border-2 " +
              "px-3 py-4 leading-tight" +
              "text-black focus:outline-none" +
              (errors.message ||
              (getFieldState("message", formState).isDirty &&
                watch("message").length <= 0)
                ? " custom-grabber-error error-input-state"
                : !getFieldState("message", formState).isDirty
                  ? " custom-grabber input"
                  : " custom-grabber-success success-input-state")
            }
            id="message"
            placeholder="Zpráva"
            rows={8}
          />
          {(errors.message ??
            (getFieldState("message", formState).isDirty &&
              watch("message").length <= 0)) && (
            <span className="mt-2 text-warning-primary">
              Toto pole je povinné!
            </span>
          )}
        </div>
        <button
          type="submit"
          disabled={isSubmitted}
          className={
            "btn btn-primary ripple mt-8 w-full justify-center text-center" +
            (isSubmitted ? " !bg-success-secondary" : "")
          }
        >
          {isSubmitted ? (
            "Přidávání"
          ) : (
            <>
              Přidat zprávu <ArrowRight />
            </>
          )}
        </button>
      </FullscreenModalContentWrapper>
    </>
  );
}
